import { useEffect, useRef, useState } from "react";
import { Reveal } from "./ui-bits";

const CHIPS = [
  "How is low testosterone diagnosed?",
  "Gel vs injections — what's the difference?",
  "What monitoring does TRT need?",
  "Can TRT affect fertility?",
];

const PROVIDERS = [
  { id: "claude", label: "Claude", model: "Sonnet 5.5" },
  { id: "gpt", label: "GPT", model: "5.4" },
];

export default function AskAndrogen() {
  const [provider, setProvider] = useState("claude");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(
    (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random()}`)
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message, provider }),
      });
      if (!res.ok || !res.body) throw new Error("Stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const evt = JSON.parse(line.slice(5).trim());
          if (evt.t === "d") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + evt.c };
              return copy;
            });
          } else if (evt.t === "e") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: evt.c };
              return copy;
            });
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = { ...last, content: "The assistant is unavailable right now. Please try again shortly." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Reveal>
      <div className="card-line rounded-2xl overflow-hidden" data-testid="ask-androgen-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-7 py-4 border-b border-line">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-teal animate-pulse" />
            <p className="mono-label text-dusk">AI HEALTH LIBRARY — BETA</p>
          </div>
          <div className="flex items-center gap-2" role="group" aria-label="Choose AI engine">
            {PROVIDERS.map((p) => (
              <button
                key={p.id}
                onClick={() => setProvider(p.id)}
                disabled={streaming}
                className={`px-3.5 py-1.5 rounded-full text-xs font-mono transition-all ${
                  provider === p.id
                    ? "bg-teal/15 border border-teal/50 text-teal"
                    : "border border-line text-dusk hover:text-bone hover:border-bone/30"
                }`}
                data-testid={`ai-provider-${p.id}`}
              >
                {p.label} <span className="opacity-60">{p.model}</span>
              </button>
            ))}
          </div>
        </div>

        <div ref={scrollRef} className="h-[340px] overflow-y-auto px-5 sm:px-7 py-6 space-y-5" data-testid="ai-chat-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <p className="font-display text-2xl tracking-wide text-bone/90">Ask about testosterone health.</p>
              <p className="text-dusk text-sm mt-2 max-w-sm leading-relaxed">
                Educational answers on symptoms, testing, treatment and safety — powered by Claude
                or GPT, with clinical guardrails. Not medical advice.
              </p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-message-${m.role}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-teal/10 border border-teal/25 text-bone"
                    : "bg-elevated/60 border border-line text-mute"
                }`}
              >
                {m.content || <span className="inline-block w-2 h-4 bg-teal animate-pulse align-middle" />}
                {m.role === "assistant" && streaming && i === messages.length - 1 && m.content && (
                  <span className="inline-block w-2 h-4 bg-teal/70 animate-pulse align-middle ml-0.5" />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 sm:px-7 pb-5 pt-1 space-y-3">
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {CHIPS.map((chip, i) => (
              <button
                key={chip}
                onClick={() => send(chip)}
                disabled={streaming}
                className="shrink-0 px-3.5 py-2 rounded-full border border-line text-xs text-mute hover:text-bone hover:border-teal/40 transition-colors"
                data-testid={`ai-chip-${i}`}
              >
                {chip}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex gap-2"
          >
            <input
              className="field flex-1"
              placeholder="Ask a testosterone health question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="ai-chat-input"
              aria-label="Ask a question"
            />
            <button
              type="submit"
              className="btn-primary !px-5 !py-3 disabled:opacity-40"
              disabled={streaming || !input.trim()}
              data-testid="ai-chat-send"
              aria-label="Send question"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </form>
          <p className="text-[11px] text-dusk leading-relaxed">
            AI-generated information, educational only — not medical advice, diagnosis or
            treatment. For personal advice, book a clinical assessment.
          </p>
        </div>
      </div>
    </Reveal>
  );
}
